import { Injectable } from '@odinjs/odin';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { AppointmentScheduleInfo } from '../../../interfaces/schedules-suggestions';
import { defaultIfUndefined } from '../../../utils/object-utils';
import { showToastNotificationError } from '../../../utils/toast-notification-utils';
import UserContext from '../../../utils/user-context';
import PseudonymAppointmentModel from '../pseudonym-appointments-model';
import { ScheduleType } from '../pseudonym-types';

interface ScheduleParams {
  appointmentEstablishment: number;
  cdCategoria: string;
  cdConvenio: string;
  cdPlano: string;
  cdProcedencia?: string;
  healthCardNumber: string;
  healthCardValidity: string;
  locationId: string;
  nrSeqAgeint: number,
  patientHeight?: number;
  patientId: string;
  patientWeight?: number,
  sequenceVat?: number,
}

interface IntegratedAgendaSequenceResponse {
  value: number;
}

@Injectable
export default class PseudonymIntegratedScheduleService {

  async getIntegratedScheduleSequenceAndSaveInternalProcedures(): Promise<void> {
    if (!PseudonymAppointmentModel.integratedScheduleSequence) {
      PseudonymAppointmentModel.integratedScheduleSequence = await this.getIntegratedScheduleSequence();
    }

    if (PseudonymAppointmentModel.stepTwo.hasProcedureChange && PseudonymAppointmentModel.integratedScheduleSequence) {
      await this.saveInternalProcedures(PseudonymAppointmentModel.integratedScheduleSequence);
    }
  }

  getIntegratedScheduleSequence(): Promise<number> {
    return HttpService.get(APPLICATION_ENDPOINTS.GET_PREVIOUS_SEQ_AGENDA_INTEGRADA, { patientId: UserContext.patientId })
      .then((response) => {
        const integratedScheduleSequence = response.body as number;
        const scheduleParams = this.getScheduleParams(integratedScheduleSequence);

        return HttpService.get<IntegratedAgendaSequenceResponse>(APPLICATION_ENDPOINTS.GET_SEQ_AGENDA_INTEGRADA, { ...scheduleParams })
          .then(res => {
            const integratedScheduleSequenceObtained =  res.body?.value as number
            return integratedScheduleSequence || defaultIfUndefined(integratedScheduleSequenceObtained, 0);
          })
          .catch(() => {
            this.showToastNotificationLoadDataError();
            return 0;
          });
      })
      .catch(() => {
        this.showToastNotificationLoadDataError();
        return 0;
      });
  }

  private getScheduleParams(integratedScheduleSequence: number | undefined): ScheduleParams {
    const healthInsurance = PseudonymAppointmentModel.stepOne.healthInsurance;
    const scheduleParams: ScheduleParams = {
      appointmentEstablishment: PseudonymAppointmentModel.stepOne.appointmentEstablishment.cdEstablishment,
      cdCategoria: defaultIfUndefined(healthInsurance.categoryId, ''),
      cdConvenio: defaultIfUndefined(healthInsurance.healthInsuranceId.toString(), ''),
      cdPlano: defaultIfUndefined(healthInsurance.planId, ''),
      healthCardNumber: defaultIfUndefined(healthInsurance.healthCardNumber, ''),
      healthCardValidity: defaultIfUndefined(healthInsurance.walletExpiryDate, ''),
      locationId: '',
      nrSeqAgeint: integratedScheduleSequence || 0,
      patientId: String(UserContext.patientId),
      sequenceVat: healthInsurance.sequenceVat || 0,
    };

    this.setPriorLocationDataOnScheduleParams(scheduleParams);
    this.setAdditionalInformationOnScheduleParams(scheduleParams);

    return scheduleParams;
  }

  private setPriorLocationDataOnScheduleParams(scheduleParams: ScheduleParams): void {
    const priorLocation = PseudonymAppointmentModel.stepOne.priorLocation;

    if (priorLocation?.isVisible && priorLocation?.sequenceNumber) {
      scheduleParams.cdProcedencia = String(priorLocation?.sequenceNumber)
    } else {
      scheduleParams.cdProcedencia = ''
    }
  }

  private setAdditionalInformationOnScheduleParams(scheduleParams: ScheduleParams): void {
    const additionalInformation = PseudonymAppointmentModel.stepThree.additionalInformation;
    if (additionalInformation.heightInCentimeters && additionalInformation.weight) {
      scheduleParams.patientHeight = additionalInformation.heightInCentimeters;
      scheduleParams.patientWeight = additionalInformation.weight;
    } else {
      scheduleParams.patientHeight = 0;
      scheduleParams.patientWeight = 0;
    }
  }

  saveInternalProcedures(integratedScheduleSequence: number): Promise<void | unknown> {
    const appointmentsScheduleInfoParams = this.getAppointmentsScheduleInfoParams(integratedScheduleSequence);
    return HttpService.post(APPLICATION_ENDPOINTS.GET_SEQ_PROC_INTERNO, { nrSeqAgeint: integratedScheduleSequence, patientId: UserContext.patientId },
      { body: appointmentsScheduleInfoParams as unknown as Record<string, Value> })
      .then(() => {
        PseudonymAppointmentModel.stepTwo.hasProcedureChange = false;
      })
      .catch(() => this.showToastNotificationLoadDataError());
  }

  private getAppointmentsScheduleInfoParams(integratedScheduleSequence: number): Array<AppointmentScheduleInfo> {
    const procedures = PseudonymAppointmentModel.stepTwo.procedures;
    const params = procedures.map((procedure) => {
      if (procedure.typeProcedureCode === ScheduleType.CONSULTATION) {
        return {
          cdEspecialidade: defaultIfUndefined(procedure.specialtySequence, ''),
          cdMedico: defaultIfUndefined(procedure.physicianSequence, ''),
          gender: procedure.gender,
          hasSeqAgendaIntegrada: true,
          ieTipoItem: ScheduleType.CONSULTATION,
          nrSeqAgeint: integratedScheduleSequence,
          nrSeqMedicalArea: null,
          nrSeqProcInterno: defaultIfUndefined(procedure.internalProcedureSequence, ''),
          professional: defaultIfUndefined(procedure.professional, '')
        };
      }

      if (procedure.typeProcedureCode === ScheduleType.EXAM) {
        return {
          gender: procedure.gender,
          hasSeqAgendaIntegrada: true,
          ieLado: defaultIfUndefined(procedure?.examSide?.sideInformationDomain, ''),
          ieTipoItem: ScheduleType.EXAM,
          nrSeqAgeint: integratedScheduleSequence,
          nrSeqProcInterno: defaultIfUndefined(procedure.internalProcedureSequence, ''),
          professional: defaultIfUndefined(procedure.professional, '')
        };
      }

      if (procedure.typeProcedureCode === ScheduleType.SERVICE) {
        return {
          hasSeqAgendaIntegrada: true,
          ieTipoItem: ScheduleType.SERVICE,
          nrSeqAgeint: integratedScheduleSequence,
          nrSeqProcInterno: defaultIfUndefined(procedure.internalProcedureSequence, ''),
          professional: defaultIfUndefined(procedure.professional, '')
        }
      }

      return null;
    }).filter((param) => param != null) as Array<AppointmentScheduleInfo>;

    if (PseudonymAppointmentModel.stepAnotherPeriod.findAnotherPhysician) {
      const list =  PseudonymAppointmentModel.findAnotherPhysician.emptySuggestions;
      for (const param of params) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parameters = param as any;
        if (parameters.cdMedico) {
          for (const item of list) {
            if (parameters.cdMedico === item.cdMedico) {
              parameters.cdMedico = '';
            }
          }
        }
      }
      PseudonymAppointmentModel.findAnotherPhysician.emptySuggestions = [];
      PseudonymAppointmentModel.stepAnotherPeriod.findAnotherPhysician = false;
    }

    return params;
  }

  private showToastNotificationLoadDataError(): void {
    showToastNotificationError(MessageService.translate('tws.toast-notification.load-data-error'));
  }

}
